import { useCallback, useMemo, useState } from 'react';
import type { GridApi } from 'ag-grid-community';

import { ScenarioMember } from '@queries/scenarios';
import type { CustomGroupWithCompanyCodes } from '../../../shared/types/customGroup';
import type { UseSelectableRowGridLogicApi } from '@client-shared/agGrid/selectableRows';

import { useCustomGroupsWithCompanyCodes } from '@queries/customGroup';
import { useOnValueChange } from '../../../shared/hooks/usePrevious';

const useCustomGroupsWithCompanyCodeOptions = ({ periodName }: { periodName?: string }) => {
  const { data, ...rest } = useCustomGroupsWithCompanyCodes({ periodName });

  const options = useMemo(() => data.map(({ id, name, companyCodes }) => ({
    label: name,
    value: id,
    companyCodes,
  })), [data]);

  return {
    data: options,
    ...rest,
  };
};

const useCustomGroupDropdownProps = ({ periodName }: { periodName?: string, }) => {
  const [selectedCustomGroup, setSelectedCustomGroup] = useState<CustomGroupWithCompanyCodes | null>(null);

  const {
    data: customGroupDropdownOptions,
    isLoading: isLoadingCustomGroups,
  } = useCustomGroupsWithCompanyCodeOptions({
    periodName,
  });

  return {
    onChange: setSelectedCustomGroup,
    options: customGroupDropdownOptions,
    value: selectedCustomGroup,
    isLoading: isLoadingCustomGroups,
    clear: () => setSelectedCustomGroup(null),
  };
};

const useUpdateGridFilter = (
  selectedCustomGroup: CustomGroupWithCompanyCodes | null | undefined,
  {
    gridApi,
    selectionApi,
    isScenarioLocked,
  }: {
  gridApi?: GridApi,
  selectionApi?: UseSelectableRowGridLogicApi<ScenarioMember>,
  isScenarioLocked?: boolean,
}) => {
  const {
    selectFiltered,
    resetSelection,
  } = selectionApi ?? {};

  useOnValueChange(selectedCustomGroup, useCallback(async selectedCustomGroup => {
    const filterInstance = await gridApi?.getColumnFilterInstance('companyCode');
    filterInstance?.setModel({
      values: selectedCustomGroup ? selectedCustomGroup.companyCodes.slice() : null,
    });
    gridApi?.onFilterChanged();
    setTimeout(() => {
      if (selectedCustomGroup && !isScenarioLocked) {
        selectFiltered?.();
        return;
      }
      resetSelection?.();
    }, 0);
  }, [gridApi, selectFiltered, resetSelection, isScenarioLocked]));
};

export const useSelectCompanyCodeByCustomGroup = ({ periodName, gridApi, selectionApi, isScenarioLocked }: {
  periodName?: string,
  gridApi?: GridApi,
  selectionApi?: UseSelectableRowGridLogicApi<ScenarioMember>
  isScenarioLocked?: boolean,
}) => {
  const dropdownProps = useCustomGroupDropdownProps({ periodName });

  useUpdateGridFilter(
    dropdownProps.value,
    {
      gridApi,
      selectionApi,
      isScenarioLocked,
    });

  return {
    dropdownProps,
  };
};
